import classNames from 'classnames';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { fireTrackEvent } from '~globals/tracking';
import ArrowIcon from '~images/inline/arrow-rebrand.inline.svg';
import QuotesIcon from '~images/inline/quotes.svg';

import * as styles from './TrustPilot.module.css';
import defaultReviews from './sampleData';

type ReviewContent = {
    consumer: { displayName: string };
    id: string;
    text: string;
    stars: number;
    createdAt?: string;
};

type ReviewQueryData = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors?: any;
    data?: { getFeaturedReviews: { reviews: ReviewContent[] } };
};

let observer: IntersectionObserver;

const ReviewCard = forwardRef(function ReviewCard(
    { review, index }: { review: ReviewContent; index?: number },
    ref?: React.ForwardedRef<HTMLDivElement | null>
) {
    return (
        <div
            className={classNames(
                styles.reviewCard,
                'bg-white border border-solid border-gray-outline rounded-lg shadow-md',
                'min-w-72 md:min-w-[336px] lg:max-w-[588px] w-full',
                'h-full min-h-[328px] md:min-h-[352px] lg:min-h-72 px-1 py-6 lg:p-8',
                'gap-4 grid grid-cols-1 relative'
            )}
            ref={ref}
            data-index={index}
        >
            <div className="flex gap-1 h-full items-start justify-between w-full">
                <img src={QuotesIcon} alt="quote" aria-hidden="true" />
                <p>{review.text}</p>
                <img
                    src={QuotesIcon}
                    alt="quote"
                    aria-hidden="true"
                    className="mt-auto rotate-180"
                />
            </div>
            <p className="text-black text-16 leading-24 mx-10 lg:m-0">
                <span className="font-bold">- {review.consumer.displayName}</span>
            </p>
        </div>
    );
});

interface CardCarouselProps {
    classes?: string;
    heading?: string;
    subheading?: string;
    reviewsHyperlink?: string;
    backgroundsurface?: SupportedBackgroundColor;
}

const CardCarousel = ({
    classes,
    heading = 'Hear from customers like you',
    subheading,
    backgroundsurface = 'bg-surface-50',
}: CardCarouselProps) => {
    const headingTag = heading.replace(/\s/g, '-').toLowerCase();
    const trackingComponentName = 'Customer Review';
    const [currentPage, setCurrentPage] = useState(0);
    const [pages, setPages] = useState<ReviewContent[]>([]);
    const reviewContainer = useRef(null as HTMLDivElement | null);

    const trackOnDotClick = () => {
        fireTrackEvent({ page: { action: `${trackingComponentName} click: dots` } });
    };

    const isFirstPage = () => currentPage === 0;

    const isLastPageOf = (pageArray: Array<ReviewContent | ReviewContent[]>) => {
        return currentPage === pageArray.length - 1;
    };

    const handleScroll = (entries: Array<IntersectionObserverEntry>) => {
        entries.forEach((entry) => {
            if (entry.intersectionRatio > 0.5) {
                const newCurrentPage = Number((entry.target as HTMLDivElement).dataset.index);
                setCurrentPage(newCurrentPage);
            }
        });
    };

    const moveMobileContent = (nPage: number) => {
        if (reviewContainer.current) {
            const gapBetweenItemPx = 26;
            const scrollWidth = reviewContainer.current.scrollWidth;
            const cardWidth = reviewContainer.current.firstElementChild?.clientWidth;
            const pageWidth = (cardWidth ?? scrollWidth / pages.length) + gapBetweenItemPx;
            const newPosition = nPage * pageWidth;

            reviewContainer.current.scrollTo({ left: newPosition, behavior: 'smooth' });
            trackOnDotClick();
        }
    };

    useEffect(() => {
        const getReviews = async () => {
            try {
                const req = await fetch(`${process.env.GATSBY_CC_GQL_URL}/graphql`, {
                    method: 'POST',
                    headers: {
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Method': 'POST',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        query: `
                        query getFeaturedReviews {
                            getFeaturedReviews {
                                reviews {
                                    id
                                    text
                                    stars
                                    createdAt
                                    consumer { displayName }
                                }
                            }
                        }
                    `,
                    }),
                });

                if (!req.ok) {
                    throw req.status;
                }

                const res = (await req.json()) as ReviewQueryData;

                if (res.errors) {
                    throw res.errors;
                }

                const reviews = res.data?.getFeaturedReviews.reviews || defaultReviews;

                setPages(reviews.slice(0, 2));
            } catch (error) {
                throw new Error('Failed to fetch reviews:', error);
            }
        };

        if (pages?.length === 0) {
            getReviews().catch(console.error);
        }
    }, [pages.length]);

    useEffect(() => {
        observer = new IntersectionObserver(handleScroll, {
            root: reviewContainer.current,
            threshold: [0.25, 0.5, 0.75],
        });
    }, []);

    return (
        <section
            className={classNames(
                classes,
                backgroundsurface,
                'pt-6 pb-10 px-4 md:px-6 lg:pt-12 lg:pb-20'
            )}
        >
            <div className="max-w-[1200px] mx-auto flex flex-col justify-center items-center mb-6 lg:mb-8">
                <h2
                    className={classNames(
                        'font-bold font-poppins text-surface-primary-darker',
                        'text-24 leading-32 md:text-32 md:leading-40 lg:leading-56',
                        'mx-auto text-center w-full'
                    )}
                >
                    {heading}
                </h2>
                <h3 className="hidden lg:block font-bold font-poppins text-18 leading-28 text-primary-dark">
                    {subheading}
                </h3>
            </div>
            <div className="p-0 overflow-y-hidden mx-auto max-w-72 md:max-w-full w-full">
                <div
                    ref={reviewContainer}
                    id="reviewContainer"
                    className="flex max-w-[1200px] gap-6 overflow-y-hidden mx-auto py-2 scrollbar-hidden"
                >
                    {pages?.map((review, i) => (
                        <ReviewCard
                            key={review.id}
                            index={i}
                            ref={(el) => {
                                el && observer.observe(el);
                            }}
                            review={review}
                        />
                    ))}
                </div>
                <div className="flex md:hidden items-center justify-center mx-auto mt-4 w-full">
                    <button
                        onClick={() => moveMobileContent(currentPage - 1)}
                        disabled={isFirstPage()}
                        aria-label="see previous reviews"
                        data-wa-link="Customer Review: left arrow"
                        data-qa={`previous:${headingTag}`}
                    >
                        <ArrowIcon
                            className={classNames(
                                'rotate-180 mx-4',
                                isFirstPage() ? 'text-black' : 'text-icon-primary'
                            )}
                        />
                    </button>
                    <button
                        onClick={() => moveMobileContent(currentPage + 1)}
                        disabled={isLastPageOf(pages)}
                        aria-label="see more reviews"
                        data-wa-link="Customer Review: right arrow"
                        data-qa={`next:${headingTag}`}
                    >
                        <ArrowIcon
                            className={classNames(
                                'mx-4',
                                isLastPageOf(pages) ? 'text-black' : 'text-icon-primary'
                            )}
                        />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default CardCarousel;
